import React, { useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const Input = ({
  id,
  name,
  type = 'text',
  autoComplete,
  placeholder,
  value,
  onChange,
  className = '',
  label,
  error = '',
  leftIcon,
  rightIcon,
  onRightIconClick,
  size = 'md',
  required,
}) => {
  const hasError = !!error;
  const wrapperRef = useRef(null);
  const prevErrorRef = useRef(false);

  useEffect(() => {
    if (hasError && !prevErrorRef.current && wrapperRef.current) {
      wrapperRef.current.style.animation = 'none';
      requestAnimationFrame(() => {
        if (wrapperRef.current) {
          wrapperRef.current.style.animation = 'shake 0.35s cubic-bezier(0.16,1,0.3,1)';
        }
      });
    }
    prevErrorRef.current = hasError;
  }, [hasError]);

  return (
    <div className={`mb-5 w-full`}>
      {label && (
        <label
          htmlFor={id}
          className="block text-xs font-semibold mb-1.5 ml-0.5"
          style={{ color: '#9BA3B8', letterSpacing: '0.03em' }}
        >
          {label}
          {required && <span style={{ color: '#DC1F26', marginLeft: '3px' }}>*</span>}
        </label>
      )}
      <div
        ref={wrapperRef}
        className={`relative flex items-center rounded-xl h-[44px] ${className}`}
        style={{
          backgroundColor: '#1a1b30',
          border: `1px solid ${hasError ? 'rgba(220,31,38,0.6)' : 'rgba(255,255,255,0.08)'}`,
          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
        }}
        onFocusCapture={e => {
          if (!hasError) e.currentTarget.style.borderColor = 'rgba(0,194,255,0.4)';
          e.currentTarget.style.boxShadow = hasError
            ? '0 0 0 2px rgba(220,31,38,0.12)'
            : '0 0 0 2px rgba(0,194,255,0.08)';
        }}
        onBlurCapture={e => {
          e.currentTarget.style.borderColor = hasError ? 'rgba(220,31,38,0.6)' : 'rgba(255,255,255,0.08)';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        {leftIcon && (
          <div className="absolute left-3 flex items-center pointer-events-none" style={{ color: '#5A6278' }}>
            {leftIcon}
          </div>
        )}
        <input
          id={id}
          name={name}
          type={type}
          autoComplete={autoComplete}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          className="w-full h-full bg-transparent focus:outline-none text-sm"
          style={{
            color: '#E8EAF0',
            paddingLeft: leftIcon ? '2.25rem' : '0.875rem',
            paddingRight: rightIcon ? '2.25rem' : '0.875rem',
            caretColor: '#00C2FF',
          }}
        />
        {rightIcon && (
          <div
            className="absolute right-3 flex items-center"
            style={{ color: '#5A6278', cursor: onRightIconClick ? 'pointer' : 'default' }}
            onClick={onRightIconClick}
          >
            {rightIcon}
          </div>
        )}
      </div>
      <AnimatePresence>
        {hasError && (
          <motion.p
            initial={{ opacity: 0, y: -4, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: -4, height: 0 }}
            transition={{ duration: 0.2 }}
            className="mt-1.5 text-xs"
            style={{ color: '#F87171', paddingLeft: '2px', overflow: 'hidden' }}
          >
            {error}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Input;
